// /frontend/src/pages/admin/AdminDesignRequestDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../components/layout/AdminLayout';
import Spinner from '../../components/ui/Spinner';
import { uploadService } from '../../services/uploadService';
import { productService } from '../../services/productService';
import { FaSearch, FaPlus, FaMinus, FaTrash, FaTimes } from 'react-icons/fa';

// Helper function to convert base64 to blob for PDFs
const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

const AdminDesignRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [designRequest, setDesignRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conceptImage, setConceptImage] = useState(null);
  const [conceptNotes, setConceptNotes] = useState('');
  const [uploadingConcept, setUploadingConcept] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [savingProducts, setSavingProducts] = useState(false);
  
  // New state for variant selection
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [selectedVariantOptions, setSelectedVariantOptions] = useState({});
  const [variantSelection, setVariantSelection] = useState({});

  useEffect(() => {
    fetchDesignRequest();
  }, [id]);

  useEffect(() => {
    if (designRequest && designRequest.suggestedProducts && designRequest.suggestedProducts.length > 0) {
      setSelectedProducts(designRequest.suggestedProducts);
    }
  }, [designRequest]);

  // Search for products
  const handleSearchProducts = async () => {
    if (!searchQuery.trim()) {
      return;
    }
    
    try {
      setSearching(true);
      // Use productService instead of uploadService
      const products = await productService.searchProducts(searchQuery);
      setSearchResults(products);
    } catch (err) {
      toast.error('Failed to search products');
    } finally {
      setSearching(false);
    }
  };
  
  // Start the add product flow - check for variants first
  const handleAddProduct = (product) => {
    // If product has variants, open the variant selection modal
    if (product.variants && product.variants.length > 0) {
      setCurrentProduct(product);
      
      // Initialize the variant selection with default options
      const initialSelection = {};
      
      product.variants.forEach(variant => {
        const defaultOption = variant.options.find(opt => opt.default) || variant.options[0];
        if (defaultOption) {
          initialSelection[variant.name] = defaultOption;
        }
      });
      
      setVariantSelection(initialSelection);
      setShowVariantModal(true);
    } else {
      // If no variants, add product directly
      addProduct(product);
    }
  };
  
  // Add product to selected list (after variant selection if needed)
  const addProduct = (product, variantOptions = null) => {
    // Create a unique identifier based on product and variant
    let productKey = product._id;
    let productPrice = product.price;
    let productVariant = null;
    
    // If variants are selected, incorporate them into the key and price
    if (variantOptions) {
      // Create variant IDs array
      const variantIds = [];
      Object.entries(variantOptions).forEach(([variantType, option]) => {
        variantIds.push(option.id);
      });
      
      // Unique key combining product ID and variant IDs
      productKey = `${product._id}-${variantIds.join('-')}`;
      
      // Adjust price based on variant price differences
      productPrice = calculatePriceWithVariants(product, variantOptions);
      
      // Store variant information
      productVariant = {
        options: { ...variantOptions }
      };
    }
    
    // Check if product with these exact variants is already in list
    const existingProductIndex = selectedProducts.findIndex(p => 
      p._id === productKey || p.productId === productKey
    );
    
    if (existingProductIndex >= 0) {
      // Increment quantity if already in list
      const updatedProducts = [...selectedProducts];
      updatedProducts[existingProductIndex] = {
        ...updatedProducts[existingProductIndex],
        quantity: (updatedProducts[existingProductIndex].quantity || 1) + 1
      };
      setSelectedProducts(updatedProducts);
    } else {
      // Add new product with quantity 1
      const newProduct = {
        _id: productKey,
        productId: product._id,
        name: product.name, // Keep original name
        price: productPrice,
        imageUrl: product.images && product.images.length > 0 ? 
          (variantOptions && Object.keys(variantOptions).length > 0 ? 
            getVariantImage(product, variantOptions) : product.images[0]) 
          : '',
        quantity: 1
      };
      
      // Add variant if present
      if (productVariant) {
        newProduct.variant = productVariant;
      }
      
      setSelectedProducts([...selectedProducts, newProduct]);
    }
    
    // Clear search results and close modal if open
    setSearchResults([]);
    setSearchQuery('');
    setShowVariantModal(false);
    setCurrentProduct(null);
  };
  
  // Calculate price including variant price differences
  const calculatePriceWithVariants = (product, variantOptions) => {
    let finalPrice = product.price;
    
    // Add all price differences from selected variant options
    Object.values(variantOptions).forEach(option => {
      if (option.priceDifference) {
        finalPrice += option.priceDifference;
      }
    });
    
    return finalPrice;
  };
  
  // Get image specific to variant if available
  const getVariantImage = (product, variantOptions) => {
    // Simple implementation - just return first image
    // In a real scenario, you might look for images matching the specific variant
    return product.images && product.images.length > 0 ? product.images[0] : '';
  };
  
  // Handle selecting a variant option
  const handleSelectVariantOption = (variantType, option) => {
    setVariantSelection({
      ...variantSelection,
      [variantType]: option
    });
  };
  
  // Confirm variant selection and add product
  const confirmVariantSelection = () => {
    if (currentProduct && Object.keys(variantSelection).length > 0) {
      addProduct(currentProduct, variantSelection);
    } else {
      toast.error('Please select all required variant options');
    }
  };
  
  // Update product quantity
  const updateProductQuantity = (productId, quantity) => {
    const newQuantity = Math.max(1, parseInt(quantity) || 1);
    
    setSelectedProducts(selectedProducts.map(p => 
      (p._id === productId || p.productId === productId) 
        ? { ...p, quantity: newQuantity } 
        : p
    ));
  };
  
  // Remove product from list
  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => 
      p._id !== productId && p.productId !== productId
    ));
  };
  
  // Save selected products
  const saveSelectedProducts = async () => {
    try {
      setSavingProducts(true);
      
      // Map products to a format that matches your backend model
      const formattedProducts = selectedProducts.map(product => {
        const result = {
          productId: product.productId,
          name: product.name,
          price: product.price,
          imageUrl: product.imageUrl || (product.images && product.images.length > 0 ? product.images[0] : ''),
          quantity: product.quantity || 1
        };
        
        // Add variant information if present
        if (product.variant) {
          result.variant = product.variant;
        }
        
        return result;
      });
      
      await uploadService.updateSuggestedProducts(id, formattedProducts);
      toast.success('Product suggestions saved');
      fetchDesignRequest();
    } catch (err) {
      toast.error('Failed to save product suggestions');
    } finally {
      setSavingProducts(false);
    }
  };

  const fetchDesignRequest = async () => {
    try {
      setLoading(true);
      const data = await uploadService.getDesignRequest(id);
      setDesignRequest(data);
    } catch (err) {
      setError(err.message || 'Failed to load design request details');
      toast.error('Error loading design request');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      setUpdatingStatus(true);
      await uploadService.updateStatus(id, { status });
      toast.success(`Status updated to ${status}`);
      fetchDesignRequest();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePaymentStatusChange = async (paymentStatus) => {
    try {
      setUpdatingStatus(true);
      await uploadService.updatePaymentStatus(id, { paymentStatus });
      toast.success(`Payment status updated to ${paymentStatus}`);
      fetchDesignRequest();
    } catch (err) {
      toast.error('Failed to update payment status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConceptImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setConceptImage(e.target.files[0]);
    }
  };

  const handleUploadConcept = async (e) => {
    e.preventDefault();
    
    if (!conceptImage) {
      toast.error('Please select an image to upload');
      return;
    }
    
    try {
      setUploadingConcept(true);
      
      const formData = new FormData();
      formData.append('conceptImage', conceptImage);
      if (conceptNotes.trim()) {
        formData.append('notes', conceptNotes);
      }
      
      await uploadService.uploadDesignConcept(id, formData);
      
      toast.success('Design concept uploaded successfully');
      setConceptImage(null);
      setConceptNotes('');
      fetchDesignRequest();
    } catch (err) {
      toast.error('Failed to upload concept');
    } finally {
      setUploadingConcept(false);
    }
  };

  // View PDF files
  const viewPdf = (data, contentType) => {
    const blob = b64toBlob(data, contentType);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
          <p>{error}</p>
        </div>
        <button
          onClick={() => navigate('/admin/design-requests')}
          className="text-teal-600 hover:text-teal-900"
        >
          ← Back to Design Requests
        </button>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Variant Selection Modal */}
      {showVariantModal && currentProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Select Variants for {currentProduct.name}</h3>
              <button 
                onClick={() => setShowVariantModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="mb-4">
              {currentProduct.variants?.map((variantType, typeIndex) => (
                <div key={typeIndex} className="mb-4">
                  <h4 className="font-medium text-gray-800 mb-2">{variantType.name}:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {variantType.options?.map((option, optIndex) => (
                      <div 
                        key={optIndex}
                        onClick={() => handleSelectVariantOption(variantType.name, option)}
                        className={`border rounded p-3 cursor-pointer hover:bg-gray-50 ${
                          variantSelection[variantType.name]?.id === option.id
                            ? 'border-teal-500 bg-teal-50' 
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="font-medium">{option.name}</div>
                        {option.priceDifference !== 0 && option.priceDifference !== undefined && (
                          <div className="text-sm text-gray-500">
                            {option.priceDifference > 0 ? `+${option.priceDifference.toFixed(2)}` : `-${Math.abs(option.priceDifference).toFixed(2)}`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Preview of final selection */}
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <div className="font-medium">Selected options:</div>
                <div className="text-sm text-gray-600">
                  {Object.entries(variantSelection).length > 0 ? 
                    Object.entries(variantSelection).map(([type, option]) => (
                      <div key={type} className="flex justify-between">
                        <span>{type}:</span>
                        <span>{option.name}</span>
                      </div>
                    ))
                    : 
                    <div className="text-gray-500 italic">No options selected</div>
                  }
                </div>
                
                {/* Final price */}
                <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between">
                  <span className="font-medium">Final Price:</span>
                  <span className="font-medium">
                    ${calculatePriceWithVariants(currentProduct, variantSelection).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowVariantModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmVariantSelection}
                disabled={Object.keys(variantSelection).length === 0}
                className={`px-4 py-2 rounded text-white ${
                  Object.keys(variantSelection).length === 0 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                Add to List
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex justify-between items-center">
        <button
          onClick={() => navigate('/admin/design-requests')}
          className="text-teal-600 hover:text-teal-900"
        >
          ← Back to Design Requests
        </button>
        
        <div className="flex space-x-2">
          {/* Status update dropdown */}
          <select
            value={designRequest.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={updatingStatus}
            className="border border-gray-300 rounded px-3 py-2"
          >
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="review">Review</option>
            <option value="revision">Revision</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
          
          {/* Payment status update dropdown */}
          <select
            value={designRequest.paymentStatus}
            onChange={(e) => handlePaymentStatusChange(e.target.value)}
            disabled={updatingStatus}
            className="border border-gray-300 rounded px-3 py-2"
          >
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>
      
      {/* Request Overview */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-lg font-semibold">Request Overview</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium mb-4">Request Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Request ID:</span>
                  <span>{designRequest._id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span>{new Date(designRequest.createdAt).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    designRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    designRequest.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    designRequest.status === 'review' ? 'bg-purple-100 text-purple-800' :
                    designRequest.status === 'revision' ? 'bg-orange-100 text-orange-800' :
                    designRequest.status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {designRequest.status.replace('_', ' ').charAt(0).toUpperCase() + designRequest.status.replace('_', ' ').slice(1)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    designRequest.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                    designRequest.paymentStatus === 'refunded' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {designRequest.paymentStatus.charAt(0).toUpperCase() + designRequest.paymentStatus.slice(1)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Design Fee:</span>
                  <span>${designRequest.designFee}.00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Revisions Used:</span>
                  <span>{designRequest.revisionCount} / 2</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-medium mb-4">Customer Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span>{designRequest.user?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span>{designRequest.user?.email || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Floor Plan */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-lg font-semibold">Floor Plan</h2>
        </div>
        <div className="p-6">
          {designRequest.floorPlan ? (
            <div className="flex flex-col items-center">
              {designRequest.floorPlan.contentType && designRequest.floorPlan.contentType.startsWith('image/') ? (
                <img 
                  src={`data:${designRequest.floorPlan.contentType};base64,${designRequest.floorPlan.data}`}
                  alt="Floor Plan"
                  className="max-w-full h-auto rounded border"
                  style={{ maxHeight: '400px' }}
                />
              ) : designRequest.floorPlan.contentType === 'application/pdf' ? (
                <button
                  onClick={() => viewPdf(designRequest.floorPlan.data, designRequest.floorPlan.contentType)}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 mb-4"
                >
                  View Floor Plan PDF
                </button>
              ) : (
                <div className="text-center text-gray-500 p-4">
                  Floor plan file available (CAD format)
                </div>
              )}
              <p className="text-sm text-gray-500 mt-2">
                Original filename: {designRequest.floorPlan.name}
              </p>
            </div>
          ) : (
            <div className="text-center text-gray-500 p-4">
              No floor plan available
            </div>
          )}
        </div>
      </div>
      
      {/* Upload Design Concept */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-lg font-semibold">Upload Design Concept</h2>
        </div>
        <div className="p-6">
          <form onSubmit={handleUploadConcept} encType="multipart/form-data">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Concept Image
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleConceptImageChange}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
              {conceptImage && (
                <p className="mt-1 text-sm text-gray-500">
                  Selected: {conceptImage.name} ({(conceptImage.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={conceptNotes}
                onChange={(e) => setConceptNotes(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
                rows="3"
                placeholder="Add any notes about this design concept..."
              ></textarea>
            </div>
            <button
              type="submit"
              disabled={uploadingConcept || !conceptImage}
              className={`px-4 py-2 rounded text-white ${
                uploadingConcept || !conceptImage 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {uploadingConcept ? 'Uploading...' : 'Upload Concept'}
            </button>
          </form>
        </div>
      </div>
      
      {/* Design Concepts */}
      {designRequest.designConcepts && designRequest.designConcepts.length > 0 && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-lg font-semibold">Design Concepts</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {designRequest.designConcepts.map((concept, index) => (
                <div key={index} className="border rounded-lg overflow-hidden">
                  <div className="p-3 bg-gray-50 border-b flex justify-between items-center">
                    <h3 className="font-medium">Concept {index + 1}</h3>
                    <span className="text-xs text-gray-500">
                      {new Date(concept.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="p-4">
                    {concept.contentType && concept.contentType.startsWith('image/') ? (
                      <img 
                        src={`data:${concept.contentType};base64,${concept.data}`}
                        alt={`Design Concept ${index + 1}`}
                        className="w-full h-auto object-contain rounded"
                        style={{ maxHeight: '300px' }}
                      />
                    ) : concept.contentType === 'application/pdf' ? (
                      <div className="text-center mb-4">
                        <button
                          onClick={() => viewPdf(concept.data, concept.contentType)}
                          className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                        >
                          View Concept PDF
                        </button>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 mb-4">
                        Concept {index + 1} file available
                      </div>
                    )}
                    
                    {concept.notes && (
                      <div className="mt-4 bg-gray-50 p-3 rounded-md">
                        <h4 className="font-medium text-sm mb-1">Notes:</h4>
                        <p className="text-gray-700 text-sm">{concept.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Revision Requests */}
      {designRequest.revisions && designRequest.revisions.length > 0 && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-lg font-semibold">Customer Revision Requests</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {designRequest.revisions.map((revision, index) => (
                <div key={index} className="border rounded-lg overflow-hidden">
                  <div className="p-3 bg-gray-50 border-b flex justify-between items-center">
                    <h3 className="font-medium">Revision Request {index + 1}</h3>
                    <span className="text-xs text-gray-500">
                      {new Date(revision.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-700">{revision.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Payment Evidence */}
      {designRequest.paymentEvidence && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
          <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold">Payment Evidence</h2>
            {(designRequest.paymentStatus === 'pending' || designRequest.paymentStatus === 'rejected') && (
              <div>
                <button
                  onClick={() => handlePaymentStatusChange('paid')}
                  className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 mr-2"
                >
                  Approve Payment
                </button>
                <button
                  onClick={() => handlePaymentStatusChange('rejected')}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  Reject Payment
                </button>
              </div>
            )}
          </div>
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center mb-2">
                <span className="text-gray-600 mr-2">Payment Method:</span>
                <span className="font-medium">
                  {designRequest.paymentMethod === 'wire_transfer' 
                    ? 'Wire Transfer' 
                    : designRequest.paymentMethod === 'cheque' 
                      ? 'Cheque' 
                      : designRequest.paymentMethod || 'Not specified'}
                </span>
              </div>
              <div className="flex items-center mb-2">
                <span className="text-gray-600 mr-2">Uploaded:</span>
                <span>{new Date(designRequest.paymentEvidenceUploadedAt).toLocaleString()}</span>
              </div>
              <div className="flex items-center mb-2">
                <span className="text-gray-600 mr-2">Status:</span>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  designRequest.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                  designRequest.paymentStatus === 'refunded' ? 'bg-gray-100 text-gray-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {designRequest.paymentStatus.charAt(0).toUpperCase() + designRequest.paymentStatus.slice(1)}
                </span>
              </div>
            </div>
            
            <div className="mt-4">
              {designRequest.paymentEvidence.contentType && designRequest.paymentEvidence.contentType.startsWith('image/') ? (
                <div>
                  <h3 className="font-medium mb-2">Payment Evidence Image:</h3>
                  <img 
                    src={`data:${designRequest.paymentEvidence.contentType};base64,${designRequest.paymentEvidence.data}`}
                    alt="Payment Evidence"
                    className="max-w-full h-auto rounded border"
                    style={{ maxHeight: '400px' }}
                  />
                </div>
              ) : designRequest.paymentEvidence.contentType === 'application/pdf' ? (
                <div>
                  <h3 className="font-medium mb-2">Payment Evidence PDF:</h3>
                  <button
                    onClick={() => viewPdf(designRequest.paymentEvidence.data, designRequest.paymentEvidence.contentType)}
                    className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                  >
                    View PDF
                  </button>
                </div>
              ) : (
                <p className="text-gray-600">Payment evidence uploaded</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Suggestions */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-lg font-semibold">Suggested Products</h2>
        </div>
        <div className="p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Products
              </label>
              <div className="flex">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-l-md px-3 py-2"
                    placeholder="Search products by name, code, category..."
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchProducts()}
                  />
                  <button
                    onClick={handleSearchProducts}
                    disabled={searching || !searchQuery.trim()}
                    className={`px-4 py-2 rounded-r-md ${
                        searching || !searchQuery.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {searching ? 'Searching...' : <FaSearch />}
                  </button>
              </div>
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mb-6">
                <h3 className="font-medium mb-2">Search Results</h3>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Variants</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {searchResults.map(product => (
                          <tr key={product._id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 whitespace-nowrap">
                              <div className="flex items-center">
                                {product.images && product.images.length > 0 && (
                                  <img 
                                    src={product.images[0]} 
                                    alt={product.name} 
                                    className="w-10 h-10 object-cover rounded mr-3"
                                  />
                                )}
                                <div>
                                  <div className="font-medium text-gray-900">{product.name}</div>
                                  <div className="text-xs text-gray-500">{product.code || 'No code'}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              ${product.price?.toFixed(2) || '0.00'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {product.variants && product.variants.length > 0 ? (
                                <div>
                                  {product.variants.map((variant, idx) => (
                                    <div key={idx} className="mb-1">
                                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                        {variant.name}: {variant.options.length} options
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">No variants</span>
                              )}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <button
                                onClick={() => handleAddProduct(product)}
                                className="px-2 py-1 bg-teal-100 text-teal-800 rounded hover:bg-teal-200 inline-flex items-center"
                              >
                                <FaPlus className="mr-1" size={12} />
                                Add
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            {/* Selected Products */}
            <div>
              <h3 className="font-medium mb-2">Selected Products</h3>
              {selectedProducts.length === 0 ? (
                <div className="text-gray-500 border border-dashed border-gray-300 rounded-lg p-6 text-center">
                  No products selected. Use the search above to find and add products.
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Variant</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedProducts.map(product => (
                          <tr key={product._id || product.productId} className="hover:bg-gray-50">
                            <td className="px-4 py-2 whitespace-nowrap">
                              <div className="flex items-center">
                                {product.imageUrl && (
                                  <img 
                                    src={product.imageUrl} 
                                    alt={product.name} 
                                    className="w-10 h-10 object-cover rounded mr-3"
                                  />
                                )}
                                <div className="font-medium text-gray-900">
                                  {product.variant ? product.name.split(' - ')[0] : product.name}
                                </div>
                              </div>
                            </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  {product.variant && product.variant.options ? (
                                    <div className="text-xs">
                                      {Object.entries(product.variant.options).map(([type, option], idx) => (
                                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full mr-1 mb-1 inline-block">
                                          {type}: {option.name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500 text-xs">Standard</span>
                                  )}
                                </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              ${parseFloat(product.price).toFixed(2)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => updateProductQuantity(product._id || product.productId, (product.quantity || 1) - 1)}
                                  className="px-1 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                                  disabled={product.quantity <= 1}
                                >
                                  <FaMinus size={10} />
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  value={product.quantity || 1}
                                  onChange={(e) => updateProductQuantity(product._id || product.productId, e.target.value)}
                                  className="w-12 text-center border border-gray-300 rounded px-1 py-1"
                                />
                                <button
                                  onClick={() => updateProductQuantity(product._id || product.productId, (product.quantity || 1) + 1)}
                                  className="px-1 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                                >
                                  <FaPlus size={10} />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              ${((product.price || 0) * (product.quantity || 1)).toFixed(2)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <button
                                onClick={() => removeProduct(product._id || product.productId)}
                                className="px-2 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 inline-flex items-center"
                              >
                                <FaTrash className="mr-1" size={12} />
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-bold">
                          <td colSpan="4" className="px-4 py-2 text-right">
                            Total:
                          </td>
                          <td className="px-4 py-2">
                            ${selectedProducts.reduce((total, product) => total + ((product.price || 0) * (product.quantity || 1)), 0).toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              <div className="mt-4 flex justify-end">
                <button
                  onClick={saveSelectedProducts}
                  disabled={savingProducts || selectedProducts.length === 0}
                  className={`px-4 py-2 rounded ${
                    savingProducts || selectedProducts.length === 0 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {savingProducts ? 'Saving...' : 'Save Product Suggestions'}
                </button>
              </div>
            </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDesignRequestDetail;